import React, { PropsWithChildren, useMemo, useState } from "react";
import { usePortfolioAccountsData } from "@shared/features/portfolio/hooks";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context";
import { ClientStatus, SortingOrder } from "@shared/types";
import {
  AccountsSort,
  PortfolioAccountData,
} from "@shared/features/portfolio/types";
import { getSortedDataBySortSelect } from "@shared/utils/general";
import { useLayoutChangeButtons } from "utils/hooks/misc";

const ACCOUNTS_PAGE_LAYOUT_CHANGE_KEY = "portfolio_accounts_layout";

const AccountsPageProvider = ({ children }: PropsWithChildren) => {
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.DESCEND
  );
  const [sortBy, setSortBy] = useState<AccountsSort>(AccountsSort.TOTAL);
  const { isCardsView, setIsCardsView } = useLayoutChangeButtons(
    ACCOUNTS_PAGE_LAYOUT_CHANGE_KEY
  );

  const {
    portfolioData,
    isLoading,
    isFetching,
    refetch,
    isLoadingPortfolioData,
  } = usePortfolioAccountsData();

  const accounts = useMemo(() => {
    return getSortedDataBySortSelect<PortfolioAccountData, AccountsSort>(
      portfolioData || [],
      sortBy,
      sortDirection,
      SORT_TO_PATH,
      [AccountsSort.NAME],
      undefined,
      (a, b) => {
        const isValidSort =
          +(b.client_status === ClientStatus.ACTIVE) -
          +(a.client_status === ClientStatus.ACTIVE);
        const balanceSort = (b.total_in_usd || 0) - (a.total_in_usd || 0);
        return isValidSort || balanceSort;
      }
    );
  }, [portfolioData, sortBy, sortDirection]);

  const value = useMemo(() => {
    return {
      accounts,
      sortBy,
      setSortBy,
      sortDirection,
      setSortDirection,
      isLoading,
      isFetching,
      refetch,
      isLoadingPortfolioData,
      isCardsView,
      setIsCardsView,
    };
  }, [
    accounts,
    sortBy,
    sortDirection,
    isLoading,
    isFetching,
    isLoadingPortfolioData,
    isCardsView,
    refetch,
    setIsCardsView,
  ]);

  return (
    <AccountsPageContext.Provider value={value}>
      {children}
    </AccountsPageContext.Provider>
  );
};

export default AccountsPageProvider;

const SORT_TO_PATH: Record<AccountsSort, string> = {
  [AccountsSort.NAME]: "client_name",
  [AccountsSort.STATUS]: "client_status",
  [AccountsSort.CHANGE]: "graph_data.change[0]",
  [AccountsSort.TOTAL]: "total_in_usd",
  [AccountsSort.AVAILABLE]: "available_in_usd",
};
